import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	Column,
	DistributiveOmit,
	If,
	Row,
	StyleProps,
	Tooltip,
	TooltipButton,
	useUpdatingRef,
} from "@hex-insights/core";
import { FieldIf, Form, FormEditButtonPair, FormState, SubmitButton, useInternalFields } from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import {
	ContractLineItemCreateMutation,
	ContractLineItemDetailQuery,
	ContractLineItemUpdateMutation,
} from "../../../Utilities";
import { ContractLineItemFormat } from "../../Format";
import { ContractLineItemFormState, ContractLineItemFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<ContractLineItemCreateMutation["createContractLineItem"]>,
	"formState" | "create"
> & {
	formStateArgs?: ContractLineItemFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the ContractLineItem model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<ContractLineItemFormValues.Create>;
	create: CreateFunction<ContractLineItemFormValues.Create, T>;
	onSuccess: (formState: FormState<ContractLineItemFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<ContractLineItemFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the ContractLineItem model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "contractLineItem.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						{formState.formValues.isDiscount ? "Add Discount" : "Add Service"}
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

const createHiddenFields: (keyof ContractLineItemFormValues.Create)[] = [
	"displayOrder",
	"isDiscount",
	"description",
	"discountID",
	"parentContractLineItemID",
];

type CreateFieldsProps = {
	formState: FormState<ContractLineItemFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	useInternalFields(formState, createHiddenFields);

	return (
		<React.Fragment>
			<Field.Contract.Select formState={formState} />
			<Field.Service.Select formState={formState} />
			<Field.Amount formState={formState} />
			<Field.DateAdded formState={formState} />
			<Field.ChildContractLineItems.SubForm formState={formState} label="Discounts" />
		</React.Fragment>
	);
}

export type ControlledChildDiscountCreateProps = Omit<
	ChildDiscountCreateProps<ContractLineItemCreateMutation["createContractLineItem"]>,
	"formState" | "create"
> & {
	formStateArgs?: ContractLineItemFormState.UseCreateFormStateArgs;
};

export function ControlledChildDiscountCreate({ formStateArgs, ...props }: ControlledChildDiscountCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <ChildDiscountCreate formState={formState} create={create} {...props} />;
}

export type ChildDiscountCreateProps<T> = {
	formState: FormState<ContractLineItemFormValues.Create>;
	create: CreateFunction<ContractLineItemFormValues.Create, T>;
	onSuccess: (formState: FormState<ContractLineItemFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<ContractLineItemFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

export function ChildDiscountCreate<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	...props
}: ChildDiscountCreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "contractLineItem.createChildDiscount" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateChildDiscountFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Add Discount
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

const createChildDiscountHiddenFields: (keyof ContractLineItemFormValues.Create)[] = [
	"displayOrder",
	"isDiscount",
	"description",
	"dateAdded", // for now
	"contractID",
	"serviceID",
	"parentContractLineItemID",
	"childContractLineItems",
];

type CreateChildDiscountFieldsProps = {
	formState: FormState<ContractLineItemFormValues.Create>;
};

function CreateChildDiscountFields({ formState }: CreateChildDiscountFieldsProps) {
	useInternalFields(formState, createChildDiscountHiddenFields);

	return (
		<React.Fragment>
			<Field.Discount.Select formState={formState} basisPrice={0} />
			<Field.Amount formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<ContractLineItemUpdateMutation["updateContractLineItem"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<ContractLineItemFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the ContractLineItem model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, contractLineItem: props.contractLineItem });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	contractLineItem: ContractLineItemDetailQuery["contractLineItem"];
	formState: FormState<ContractLineItemFormValues.Detail>;
	update: UpdateFunction<ContractLineItemFormValues.Detail, T>;
	onSuccess: (formState: FormState<ContractLineItemFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<ContractLineItemFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the ContractLineItem model using the given form state.
 */
export function Detail<T>({
	formState,
	contractLineItem,
	update,
	onSuccess,
	formNameSuffix,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `contractLineItem.detail.${contractLineItem.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} contractLineItem={contractLineItem} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<ContractLineItemFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the ContractLineItem model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, contractLineItem: props.contractLineItem });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	contractLineItem: ContractLineItemDetailQuery["contractLineItem"];
	formState: FormState<ContractLineItemFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the ContractLineItem model using the given form state.
 */
export function ReadOnly({ formState, contractLineItem, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} contractLineItem={contractLineItem} />
			</Form.Column>
		</div>
	);
}

const detailHiddenFields: (keyof ContractLineItemFormValues.Detail)[] = ["isDiscount", "displayOrder"];

type DetailFieldsProps = {
	contractLineItem: ContractLineItemDetailQuery["contractLineItem"];
	formState: FormState<ContractLineItemFormValues.Detail>;
};

function DetailFields({ formState, contractLineItem }: DetailFieldsProps) {
	useInternalFields(formState, detailHiddenFields);

	return (
		<React.Fragment>
			<FieldIf formState={formState} name="contractID" condition={!!formState.formValues.contractID}>
				<Field.Contract.Select formState={formState} currentInstance={contractLineItem.contract} />
			</FieldIf>
			<FieldIf
				formState={formState}
				name="parentContractLineItemID"
				condition={!!formState.formValues.parentContractLineItemID}
			>
				<Field.ParentContractLineItem.Select
					formState={formState}
					currentInstance={contractLineItem.parentContractLineItem}
				/>
			</FieldIf>
			<FieldIf formState={formState} name="serviceID" condition={!!formState.formValues.serviceID}>
				<Field.Service.Select formState={formState} currentInstance={contractLineItem.service} />
			</FieldIf>
			<FieldIf formState={formState} name="discountID" condition={!!formState.formValues.discountID}>
				<Field.Discount.Select formState={formState} currentInstance={contractLineItem.discount} basisPrice={null} />
			</FieldIf>
			<Field.Description formState={formState} />
			<Field.Amount formState={formState} />
			<Field.DateAdded formState={formState} />
			<Field.DateRemoved formState={formState} />
		</React.Fragment>
	);
}

const internalFieldsInContract: (keyof ContractLineItemFormValues.CreateInContract)[] = [
	"displayOrder",
	"description",
	"isDiscount",
	"discountID",
	"parentContractLineItemID",
	"dateAdded",
];

export type CreateFieldsInContractProps = {
	formState: FormState<ContractLineItemFormValues.CreateInContract>;
};

export function CreateFieldsInContract({ formState }: CreateFieldsInContractProps) {
	useInternalFields(formState, internalFieldsInContract);

	const discountTotal = React.useMemo(() => {
		let total = 0;
		for (let i = 0; i < formState.formValues.childContractLineItems.length; i++) {
			const lineItem = formState.formValues.childContractLineItems[i];
			total += lineItem.amount ?? 0;
		}
		return total;
	}, [formState.formValues.childContractLineItems]);
	const hasChildren = formState.formValues.childContractLineItems.length > 0;

	const formStateRef = useUpdatingRef(formState);
	const onTooltipOpenChange = React.useCallback(
		(isOpen: boolean) => {
			const {
				formValues: { childContractLineItems },
				formSetFunctions: { childContractLineItems: setChildContractLineItems },
			} = formStateRef.current;
			if (isOpen) {
				if (childContractLineItems.length === 0) {
					setChildContractLineItems((prev) => [...prev, ContractLineItemFormValues.blankItemInParentContractLineItem]);
				}
			} else {
				if (childContractLineItems.some((e) => e.discountID === null)) {
					setChildContractLineItems((prev) => prev.filter((e) => e.discountID !== null));
				}
			}
		},
		[formStateRef],
	);

	return (
		<React.Fragment>
			<Field.Service.Select formState={formState} noHeader />
			<Field.Amount formState={formState} noHeader />
			<Row justify="spaced-start">
				<If condition={hasChildren}>
					<div>{ContractLineItemFormat.Fields.amount(discountTotal)}</div>
				</If>
				<TooltipButton onOpenChange={onTooltipOpenChange}>
					{({ close }) => (
						<Tooltip isInteractable style={{ "--tooltip---font-size": "1rem" } as React.CSSProperties}>
							<Tooltip.Header style={{ padding: "1rem 1rem 0" }}>
								<strong>Discounts</strong>
							</Tooltip.Header>
							<Tooltip.Body style={{ padding: "0.5rem 1rem" }}>
								<Column justify="spaced-start">
									<Field.ChildContractLineItems.SubForm formState={formState} noHeader />

									<span>
										Discount Total: <strong>{ContractLineItemFormat.Fields.amount(discountTotal)}</strong>
									</span>
								</Column>
							</Tooltip.Body>
							<Tooltip.Footer style={{ padding: "0 1rem 1rem" }}>
								<Row justify="flex-end">
									<Button variant="primary" onClick={close}>
										Done
									</Button>
								</Row>
							</Tooltip.Footer>
						</Tooltip>
					)}
					{({ onClick }) => (
						<Button variant="link" size="small" onClick={onClick}>
							{hasChildren ? (
								<Icon.PencilSimple size={16} className="block" />
							) : (
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.Plus size={16} /> Add Discount
								</Row>
							)}
						</Button>
					)}
				</TooltipButton>
			</Row>
		</React.Fragment>
	);
}

const internalFieldsInParent: (keyof ContractLineItemFormValues.CreateInParentContractLineItem)[] = [
	"displayOrder",
	"description",
	"isDiscount",
	"serviceID",
	"childContractLineItems",
	"contractID",
	"dateAdded",
];

export type CreateFieldsInParentContractLineItemProps = {
	formState: FormState<ContractLineItemFormValues.CreateInParentContractLineItem>;
	basisPrice: number | null;
};

export function CreateFieldsInParentContractLineItem({
	formState,
	basisPrice,
}: CreateFieldsInParentContractLineItemProps) {
	useInternalFields(formState, internalFieldsInParent);

	return (
		<React.Fragment>
			<Field.Discount.Select formState={formState} noHeader basisPrice={basisPrice} />
			<Field.Amount formState={formState} noHeader />
		</React.Fragment>
	);
}
